import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ENVELOPES } from "@/constants/testIds";
import { softDeleteWithUndo, toast } from "@/lib/notify";
import { X, Plus, Trash2, AlertTriangle, ArrowRightCircle, Search, CheckCircle2 } from "lucide-react";

const fmt = (n) =>
  Number(n || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const EMPTY = {
  member_id: "",
  visitor_name: "",
  type: "Diezmo",
  amount: 0,
  notes: "",
};

const norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export default function EnvelopesModal({ open, onClose, capture, members, onApplyTotals }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [applyMsg, setApplyMsg] = useState("");
  const [applying, setApplying] = useState(false);

  // Autocomplete state
  const [memberQuery, setMemberQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!open || !capture?.id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/envelopes", {
          params: { capture_id: capture.id },
        });
        if (alive) setItems(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Error cargando sobres");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, capture?.id]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setForm(EMPTY);
      setSelectedMember(null);
      setMemberQuery("");
      setShowSuggestions(false);
      setApplyMsg("");
      setError("");
    }
  }, [open]);

  const totals = useMemo(() => {
    const t = { Diezmo: 0, Ofrenda: 0 };
    items.forEach((i) => {
      t[i.type] = (t[i.type] || 0) + (Number(i.amount) || 0);
    });
    return t;
  }, [items]);

  const captureTotals = useMemo(
    () => ({
      Diezmo: Number(capture?.tithes || 0),
      Ofrenda:
        Number(capture?.offering || 0) + Number(capture?.special_offering || 0),
    }),
    [capture]
  );

  const mismatchDiezmo = Math.abs(totals.Diezmo - captureTotals.Diezmo) > 0.005;
  const mismatchOfrenda = Math.abs(totals.Ofrenda - captureTotals.Ofrenda) > 0.005;

  // Autocomplete suggestions: match query against name + custom_id (accent-insensitive)
  const suggestions = useMemo(() => {
    const q = norm(memberQuery).trim();
    if (!q) return [];
    return members
      .filter((m) => {
        const hay = `${norm(m.name)} ${norm(m.custom_id)}`;
        // Allow multiple words: every word must appear
        return q.split(/\s+/).every((w) => hay.includes(w));
      })
      .slice(0, 8);
  }, [memberQuery, members]);

  const resetForm = () => {
    setForm(EMPTY);
    setSelectedMember(null);
    setMemberQuery("");
    setShowSuggestions(false);
  };

  const handlePickMember = (m) => {
    setSelectedMember(m);
    setForm((f) => ({ ...f, member_id: m.id, visitor_name: "" }));
    setMemberQuery("");
    setShowSuggestions(false);
    setFocusIdx(0);
  };

  const handleClearMember = () => {
    setSelectedMember(null);
    setForm((f) => ({ ...f, member_id: "" }));
    setMemberQuery("");
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const m = suggestions[focusIdx];
      if (m) handlePickMember(m);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleAdd = async () => {
    setError("");
    const amount = Number(form.amount) || 0;
    if (amount <= 0) {
      setError("El monto debe ser mayor que 0");
      return;
    }
    if (!form.member_id && !form.visitor_name.trim()) {
      setError("Selecciona un miembro o escribe el nombre del visitante");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        capture_id: capture.id,
        member_id: form.member_id || null,
        visitor_name: form.member_id ? "" : form.visitor_name.trim(),
        type: form.type,
        amount,
        notes: form.notes || "",
      };
      const { data } = await api.post("/envelopes", payload);
      setItems((prev) => [...prev, data]);
      resetForm();
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error guardando sobre");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    const row = items.find((i) => i.id === id);
    if (!row) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
    softDeleteWithUndo({
      message: "Sobre eliminado",
      description: row.donor_name
        ? `${row.donor_name} · $${row.tithes || 0} / $${row.offerings || 0}`
        : undefined,
      onUndo: () => {
        setItems((prev) => (prev.some((i) => i.id === id) ? prev : [row, ...prev]));
      },
      onDelete: async () => {
        try {
          await api.delete(`/envelopes/${id}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error eliminando — restaurando");
          setItems((prev) => (prev.some((i) => i.id === id) ? prev : [row, ...prev]));
        }
      },
    });
  };

  const handleGenerate = async (kind) => {
    if (!onApplyTotals || !capture?.id) return;
    setApplying(true);
    setError("");
    setApplyMsg("");
    try {
      // kind: "tithes" | "offering"
      const value = kind === "tithes" ? totals.Diezmo : totals.Ofrenda;
      await onApplyTotals({ kind, value });
      setApplyMsg(
        kind === "tithes"
          ? `Diezmos aplicados a Captura Masiva: $ ${fmt(value)}`
          : `Ofrendas aplicadas a Captura Masiva: $ ${fmt(value)}`
      );
      setTimeout(() => setApplyMsg(""), 3500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error aplicando totales a la captura");
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid={ENVELOPES.modal}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#f5f0e6] w-full max-w-5xl max-h-[90vh] flex flex-col border border-[#d9cfb8] shadow-2xl"
        style={{ borderTop: "4px solid #c8a04a" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#d9cfb8] bg-white">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#8b7e5e]">
              Sobres del servicio
            </div>
            <h2 className="font-serif text-2xl text-[#1c1a14]">
              {capture?.date} · {capture?.service_type}
            </h2>
          </div>
          <button
            data-testid={ENVELOPES.closeButton}
            onClick={onClose}
            className="text-[#5d5849] hover:text-[#1c1a14]"
          >
            <X size={22} />
          </button>
        </div>

        {/* Totals + warnings + generate buttons */}
        <div className="px-6 py-3 bg-white border-b border-[#d9cfb8] flex flex-wrap gap-6 items-center text-sm">
          <TotalChip
            testid={ENVELOPES.totalDiezmos}
            label="Diezmos (sobres)"
            value={totals.Diezmo}
            expected={captureTotals.Diezmo}
            mismatch={mismatchDiezmo}
          />
          <TotalChip
            testid={ENVELOPES.totalOfrendas}
            label="Ofrendas (sobres)"
            value={totals.Ofrenda}
            expected={captureTotals.Ofrenda}
            mismatch={mismatchOfrenda}
          />

          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid={ENVELOPES.generateDiezmosButton}
              onClick={() => handleGenerate("tithes")}
              disabled={applying || totals.Diezmo <= 0}
              className="flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-3 py-2 text-[11px] uppercase tracking-widest disabled:opacity-50"
              title="Pasa el total de diezmos de sobres a la casilla Diezmos de Captura Masiva"
            >
              <ArrowRightCircle size={14} /> Generar Diezmos
            </button>
            <button
              data-testid={ENVELOPES.generateOfrendasButton}
              onClick={() => handleGenerate("offering")}
              disabled={applying || totals.Ofrenda <= 0}
              className="flex items-center gap-2 border border-[#c8a04a] text-[#7a5b0f] hover:bg-[#fdf7e1] px-3 py-2 text-[11px] uppercase tracking-widest disabled:opacity-50"
              title="Pasa el total de ofrendas de sobres a la casilla Ofrenda de Captura Masiva"
            >
              <ArrowRightCircle size={14} /> Generar Ofrendas
            </button>
          </div>
        </div>

        {applyMsg && (
          <div
            data-testid={ENVELOPES.applySuccessMsg}
            className="px-6 py-2 bg-emerald-50 border-b border-emerald-200 text-emerald-700 text-xs flex items-center gap-2"
          >
            <CheckCircle2 size={14} /> {applyMsg}
          </div>
        )}

        {(mismatchDiezmo || mismatchOfrenda) && (
          <div
            data-testid={ENVELOPES.mismatchWarning}
            className="px-6 py-2.5 bg-[#fdf7e1] border-b border-[#e7c879] text-[#7a5b0f] text-xs flex items-center gap-2"
          >
            <AlertTriangle size={14} />
            Los totales de sobres no coinciden con los montos capturados en la fila. Usa{" "}
            <strong>Generar Diezmos</strong> / <strong>Generar Ofrendas</strong> para sincronizar.
          </div>
        )}

        {error && (
          <div className="px-6 py-2.5 bg-red-50 border-b border-red-200 text-red-700 text-xs">
            {error}
          </div>
        )}

        {/* Add form */}
        <div className="px-6 py-4 bg-[#fbf7ee] border-b border-[#d9cfb8] grid grid-cols-12 gap-3 items-end">
          {/* Member autocomplete */}
          <Field label="Miembro (escribe nombre, apellido o ID)" className="col-span-5 relative">
            {selectedMember ? (
              <div
                data-testid={ENVELOPES.memberSelectedBadge}
                className="flex items-center justify-between gap-2 bg-white border border-[#1c2a3a] px-3 py-2 text-sm"
              >
                <span className="truncate">
                  <span className="inline-block bg-[#1c2a3a] text-[#e7c879] text-[10px] tracking-widest uppercase px-1.5 py-0.5 mr-2">
                    {selectedMember.custom_id || "—"}
                  </span>
                  <span className="font-medium text-[#1c1a14]">{selectedMember.name}</span>
                </span>
                <button
                  data-testid={ENVELOPES.memberClearButton}
                  onClick={handleClearMember}
                  className="text-[#a14040] hover:text-red-800 shrink-0"
                  title="Quitar selección"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8b7e5e] pointer-events-none"
                  />
                  <input
                    ref={searchInputRef}
                    data-testid={ENVELOPES.memberSearchInput}
                    value={memberQuery}
                    onChange={(e) => {
                      setMemberQuery(e.target.value);
                      setShowSuggestions(true);
                      setFocusIdx(0);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ej: Andres Perez · 042"
                    className={`${inputCls} pl-8`}
                    autoComplete="off"
                  />
                </div>
                {showSuggestions && memberQuery.trim() && (
                  <ul
                    data-testid={ENVELOPES.memberSuggestionsList}
                    className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-auto bg-white border border-[#d9cfb8] shadow-lg"
                  >
                    {suggestions.length === 0 ? (
                      <li className="px-3 py-2 text-xs text-[#8b7e5e]">
                        Sin coincidencias. Si es visitante, escríbelo abajo.
                      </li>
                    ) : (
                      suggestions.map((m, idx) => (
                        <li
                          key={m.id}
                          data-testid={ENVELOPES.memberSuggestionItem(m.id)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handlePickMember(m);
                          }}
                          onMouseEnter={() => setFocusIdx(idx)}
                          className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-3 ${
                            idx === focusIdx ? "bg-[#fbf7ee]" : "hover:bg-[#fbf7ee]"
                          }`}
                        >
                          <span className="inline-block bg-[#1c2a3a] text-[#e7c879] text-[10px] tracking-widest uppercase px-1.5 py-0.5 min-w-[2.5rem] text-center">
                            {m.custom_id || "—"}
                          </span>
                          <span className="text-[#1c1a14] truncate">{m.name}</span>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
            {/* Hidden field with the selected member id for tests/legacy */}
            <input
              type="hidden"
              data-testid={ENVELOPES.memberSelect}
              value={form.member_id}
              readOnly
            />
          </Field>

          <Field label="Visitante (si no es miembro)" className="col-span-3">
            <input
              data-testid={ENVELOPES.visitorInput}
              value={form.visitor_name}
              disabled={!!form.member_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, visitor_name: e.target.value }))
              }
              placeholder="Nombre"
              className={inputCls}
            />
          </Field>

          <Field label="Tipo" className="col-span-1">
            <select
              data-testid={ENVELOPES.typeSelect}
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className={inputCls}
            >
              <option value="Diezmo">Diezmo</option>
              <option value="Ofrenda">Ofrenda</option>
            </select>
          </Field>

          <Field label="Monto" className="col-span-2">
            <input
              data-testid={ENVELOPES.amountInput}
              type="number"
              step="0.01"
              min="0"
              value={form.amount === 0 ? "" : form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
              placeholder="0.00"
              className={`${inputCls} text-right tabular-nums`}
            />
          </Field>

          <button
            data-testid={ENVELOPES.addButton}
            onClick={handleAdd}
            disabled={saving}
            className="col-span-1 flex items-center justify-center gap-1 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-3 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
          >
            <Plus size={14} /> Add
          </button>

          <div className="col-span-12">
            <input
              data-testid={ENVELOPES.notesInput}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Notas (opcional)"
              className={inputCls}
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto bg-white">
          <table className="ajar-spreadsheet w-full" data-testid={ENVELOPES.table}>
            <thead>
              <tr>
                <th style={{ width: 80 }}>ID</th>
                <th>Aportante</th>
                <th style={{ width: 110 }}>Tipo</th>
                <th className="text-right" style={{ width: 120 }}>Monto</th>
                <th>Notas</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#8b7e5e]">
                    Cargando sobres...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#8b7e5e]">
                    Sin sobres registrados para este servicio.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((it) => (
                  <tr key={it.id} data-testid={ENVELOPES.row(it.id)}>
                    <td className="text-center text-xs text-[#5d5849]">
                      {it.member_custom_id || "—"}
                    </td>
                    <td>
                      {it.member_name ||
                        (it.visitor_name
                          ? `${it.visitor_name} (visitante)`
                          : "—")}
                    </td>
                    <td>
                      <span
                        className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                          it.type === "Diezmo"
                            ? "border-[#1c2a3a] text-[#1c2a3a]"
                            : "border-[#c8a04a] text-[#7a5b0f]"
                        }`}
                      >
                        {it.type}
                      </span>
                    </td>
                    <td className="text-right tabular-nums font-medium">
                      $ {fmt(it.amount)}
                    </td>
                    <td className="text-xs text-[#5d5849]">{it.notes || "—"}</td>
                    <td className="text-center">
                      <button
                        data-testid={ENVELOPES.deleteButton(it.id)}
                        onClick={() => handleDelete(it.id)}
                        className="text-[#a14040] hover:text-red-800"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#d9cfb8] px-3 py-2 text-sm text-[#1c1a14] focus:outline-none focus:border-[#c8a04a] disabled:opacity-50";

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function TotalChip({ label, value, expected, mismatch, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
        {label}
      </span>
      <span
        className={`font-serif text-lg tabular-nums ${
          mismatch ? "text-[#a14040]" : "text-[#1c2a3a]"
        }`}
      >
        $ {fmt(value)}{" "}
        <span className="text-xs text-[#8b7e5e]">/ $ {fmt(expected)}</span>
      </span>
    </div>
  );
}
